import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import MathJaxRenderer from '../../components/MathJaxRenderer';
import { answerLabel } from '../../common/schoolApi';

/**
 * One question as Question_resolver renders it: the image (most WiZi
 * questions are images), else the text, and the options when they exist as
 * text. Used by the builder, the bank search and the result screens, so a
 * teacher sees a question the same way everywhere.
 *
 * `answer` and `given` are optional: the bank never has a key (the API does
 * not send one), a paper on a test does, a marked paper has both.
 */
export type QuestionContent = {
    source?: string;
    id?: number;
    question_img_url?: string;
    question_text?: string | null;
    option_a?: string | null;
    option_b?: string | null;
    option_c?: string | null;
    option_d?: string | null;
    option_count?: number;
    question_type?: string;
    bank_type?: string | null;
    subject_name?: string | null;
    chapter_name?: string | null;
    topic_name?: string | null;
    level?: string | null;
    year?: number | null;
    language?: string;
    solution?: string | null;
};

export const QuestionView = ({
    q,
    answer,
    given,
    compact = false,
    showSolution = false,
}: {
    q: QuestionContent | null;
    answer?: string | null;
    given?: string | null;
    compact?: boolean;
    showSolution?: boolean;
}) => {
    if (!q) {
        return (
            <Typography variant="body2" color="error">
                This question is no longer in the WiZi bank. Its answer key and marks on this paper still stand.
            </Typography>
        );
    }
    const options = (['a', 'b', 'c', 'd'] as const)
        .map((k, i) => ({ n: String(i + 1), label: 'ABCD'[i], text: (q as any)[`option_${k}`] as string | null }))
        .filter(o => o.text);

    return (
        <Box>
            {q.question_img_url ? (
                <img
                    src={q.question_img_url}
                    alt={`Question ${q.source}:${q.id}`}
                    loading="lazy"
                    style={{ maxWidth: '100%', maxHeight: compact ? 160 : 420, display: 'block' }}
                />
            ) : q.question_text ? (
                <MathJaxRenderer>{q.question_text}</MathJaxRenderer>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    (no image or text)
                </Typography>
            )}

            {!compact && options.length > 0 && (
                <Box component="ol" sx={{ listStyle: 'none', pl: 0, my: 1 }}>
                    {options.map(o => (
                        <Box
                            component="li"
                            key={o.n}
                            sx={{
                                display: 'flex',
                                gap: 1,
                                py: 0.25,
                                fontWeight: answer === o.n ? 600 : 400,
                                color: answer === o.n ? 'success.main' : given === o.n ? 'error.main' : 'inherit',
                            }}
                        >
                            <span>{o.label}.</span>
                            <MathJaxRenderer>{o.text as string}</MathJaxRenderer>
                        </Box>
                    ))}
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                <Chip size="small" variant="outlined" label={q.source === 'wizi_question' ? 'Mock bank' : 'Practice bank'} />
                {q.bank_type === 'pyq' && <Chip size="small" color="secondary" label={`PYQ${q.year ? ` ${q.year}` : ''}`} />}
                {q.question_type === 'integer' && <Chip size="small" label="Integer answer" />}
                {q.level && <Chip size="small" variant="outlined" label={q.level} />}
                {q.chapter_name && <Chip size="small" variant="outlined" label={q.chapter_name} />}
                {q.topic_name && <Chip size="small" variant="outlined" label={q.topic_name} />}
                {answer !== undefined && <Chip size="small" color="success" label={`Key: ${answerLabel(answer, q.question_type)}`} />}
            </Box>

            {showSolution && q.solution && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Solution
                    </Typography>
                    {/^https?:\/\//.test(q.solution.trim()) ? (
                        <img src={q.solution.trim()} alt="Solution" style={{ maxWidth: '100%' }} />
                    ) : (
                        <MathJaxRenderer>{q.solution}</MathJaxRenderer>
                    )}
                </Box>
            )}
        </Box>
    );
};
